import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { mdiAccountKeyOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { Button } from './ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet'
import { AssetAccessPanel } from './AssetAccessPanel'
import { InviteMemberDialog } from './InviteMemberDialog'
import { useAssetAccess } from '../api/accessGrants'

/**
 * Who has access to this asset, as a header button (spec 2026-10-25-20).
 *
 * It used to be a tab of its own; a tab for one short list of people is a page
 * nobody visits. The button says how many grants reach the asset and opens the
 * same panel in a side sheet, with the invite dialog. Only rendered for people
 * who manage access (`members.read`), as the tab was.
 */
export function AssetAccessButton({ org, assetUid, assetName, initialOpen = false }: {
    org: string
    assetUid: string
    assetName: string
    /** Opens the sheet on mount — the old `?tab=access` links land here. */
    initialOpen?: boolean
}) {
    const { t } = useTranslation('access')
    const queryClient = useQueryClient()
    const [open, setOpen] = useState(initialOpen)
    const [inviteOpen, setInviteOpen] = useState(false)
    const { data } = useAssetAccess(org, assetUid)
    const count = data?.data?.length ?? 0
    const label = t('panel.title')

    return (
        <>
            <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(true)}
                aria-label={t('panel.button_aria', { count })}
                title={t('panel.button_aria', { count })}
                data-testid="asset-access-btn"
            >
                <AppIcon path={mdiAccountKeyOutline} size="sm" className="md:mr-1" />
                <span className="hidden md:inline">{label}</span>
                {count > 0 && (
                    <span className="ml-1 rounded-full bg-muted px-1.5 text-xs font-semibold tabular-nums" data-testid="asset-access-count">
                        {count}
                    </span>
                )}
            </Button>
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" data-testid="asset-access-sheet">
                    <SheetHeader>
                        <SheetTitle>{label}</SheetTitle>
                        <SheetDescription>{assetName}</SheetDescription>
                    </SheetHeader>
                    <div className="px-4 pb-4">
                        <AssetAccessPanel org={org} assetUid={assetUid} assetName={assetName} onInvite={() => setInviteOpen(true)} />
                    </div>
                </SheetContent>
            </Sheet>
            <InviteMemberDialog
                org={org}
                open={inviteOpen}
                onOpenChange={setInviteOpen}
                defaultAsset={{ uid: assetUid, name: assetName }}
                onInvited={() => {
                    // useCreateAccessGrant already invalidates every access-grant
                    // query for the org, but the picker's selection may not be
                    // this asset (the field stays editable): refresh this
                    // asset's grants explicitly so the panel is never stale.
                    queryClient.invalidateQueries({
                        predicate: (query) => {
                            const key = query.queryKey as unknown[]
                            if (key.length < 2 || key[0] !== 'get') return false
                            if (key[1] !== '/api/v1/organizations/{orgCode}/assets/{assetUid}/access') return false
                            const params = key.find((item) => item && typeof item === 'object' && 'params' in item) as
                                | { params?: { path?: { orgCode?: string; assetUid?: string } } }
                                | undefined
                            return params?.params?.path?.orgCode === org && params?.params?.path?.assetUid === assetUid
                        },
                    })
                }}
            />
        </>
    )
}
